/**
 * How the wind pushes the boat around. Authored speed is metres per second (editor and HUD show
 * that number). The disk offset is a squared, capped, low-speed force stand-in — not the wind
 * velocity added to the boat, and not `|wind − boat|`. `windOffsetPx` is zero on a calm or
 * wind-off map, or when feel is disabled.
 */

import { MOVEMENT_RADIUS, PIXELS_PER_METER } from '../constants.ts';
import type { Environment } from '../map/environment.ts';
import { clamp } from '../math.ts';
import type { Vec2 } from '../types.ts';

export type WindFeelTuning = {
  coefficient: number;
  exposure: number;
  lowSpeedMultiplier: number;
  offsetCapM: number;
};

/** Session + factory bag. `enabled` is the play-debug A/B; it is not stored on the map. */
export type WindFeel = WindFeelTuning & {
  enabled: boolean;
};

export const WIND_COEFFICIENT_MIN = 0;
export const WIND_COEFFICIENT_MAX = 0.25;
export const WIND_COEFFICIENT_STEP = 0.005;

export const WIND_EXPOSURE_MIN = 0;
export const WIND_EXPOSURE_MAX = 3;
export const WIND_EXPOSURE_STEP = 0.1;

export const WIND_LOW_SPEED_MIN = 1;
export const WIND_LOW_SPEED_MAX = 4;
export const WIND_LOW_SPEED_STEP = 0.1;

export const WIND_OFFSET_CAP_MIN_M = 1;
export const WIND_OFFSET_CAP_MAX_M = 20;
export const WIND_OFFSET_CAP_STEP_M = 0.5;

export const DEFAULT_WIND_TUNING: WindFeelTuning = {
  coefficient: 0.05,
  exposure: 1,
  lowSpeedMultiplier: 2,
  offsetCapM: 10,
};

export const DEFAULT_WIND_FEEL: WindFeel = {
  ...DEFAULT_WIND_TUNING,
  enabled: true,
};

export function cloneWindFeel(feel: WindFeel): WindFeel {
  return { ...feel };
}

export function clampWindTuning(
  tuning: Partial<WindFeelTuning>,
  fallback: WindFeelTuning = DEFAULT_WIND_TUNING,
): WindFeelTuning {
  return {
    coefficient: clamp(
      tuning.coefficient ?? fallback.coefficient,
      WIND_COEFFICIENT_MIN,
      WIND_COEFFICIENT_MAX,
    ),
    exposure: clamp(tuning.exposure ?? fallback.exposure, WIND_EXPOSURE_MIN, WIND_EXPOSURE_MAX),
    lowSpeedMultiplier: clamp(
      tuning.lowSpeedMultiplier ?? fallback.lowSpeedMultiplier,
      WIND_LOW_SPEED_MIN,
      WIND_LOW_SPEED_MAX,
    ),
    offsetCapM: clamp(
      tuning.offsetCapM ?? fallback.offsetCapM,
      WIND_OFFSET_CAP_MIN_M,
      WIND_OFFSET_CAP_MAX_M,
    ),
  };
}

export function clampWindFeel(
  feel: Partial<WindFeel>,
  fallback: WindFeel = DEFAULT_WIND_FEEL,
): WindFeel {
  return {
    ...clampWindTuning(feel, fallback),
    enabled: feel.enabled ?? fallback.enabled,
  };
}

/** Engine heading 0 points right, compass 0 points up, which is the same offset display uses. */
export function windVectorM(directionDeg: number, speed: number): Vec2 {
  const radians = ((directionDeg - 90) * Math.PI) / 180;

  return { x: Math.cos(radians) * speed, y: Math.sin(radians) * speed };
}

/**
 * Metres of disk-centre drift this turn. Square law, then a rest-vs-speed grip factor, then a
 * playability cap. Factory 2 m/s at full speed is 0.2 m; 4 m/s is 0.8 m (4×).
 */
export function windOffsetM(
  windSpeed: number,
  boatSpeedPx: number,
  feel: WindFeel = DEFAULT_WIND_FEEL,
): number {
  if (!feel.enabled || windSpeed <= 0) {
    return 0;
  }

  const normalized = clamp(boatSpeedPx / MOVEMENT_RADIUS, 0, 1);
  const lowSpeedFactor =
    feel.lowSpeedMultiplier + (1 - feel.lowSpeedMultiplier) * normalized;

  return Math.min(
    feel.coefficient * windSpeed * windSpeed * feel.exposure * lowSpeedFactor,
    feel.offsetCapM,
  );
}

/**
 * The drift the wind adds to the movement-disk centre this turn, in world pixels.
 * `{0,0}` when wind is disabled, calm, or feel is off.
 */
export function windOffsetPx(
  environment: Pick<Environment, 'wind'>,
  boatSpeedPx: number = 0,
  feel: WindFeel = DEFAULT_WIND_FEEL,
): Vec2 {
  const { wind } = environment;

  if (!wind.enabled || wind.speed <= 0 || !feel.enabled) {
    return { x: 0, y: 0 };
  }

  const metres = windOffsetM(wind.speed, boatSpeedPx, feel);

  if (metres <= 0) {
    return { x: 0, y: 0 };
  }

  const vector = windVectorM(wind.directionDeg, metres);

  return { x: vector.x * PIXELS_PER_METER, y: vector.y * PIXELS_PER_METER };
}
